import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, HeadphonesIcon, FileText, MessageCircle, ChevronDown, ChevronUp, ArrowRight, CheckCircle, Users, Clock
} from 'lucide-react';
import { FAQS } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import Seo from '../components/Seo';

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-sm pr-4">{question}</span>
        {open
          ? <ChevronUp size={17} className="text-blue-600 shrink-0" />
          : <ChevronDown size={17} className="text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 bg-white border-t border-gray-100">
          <p className="text-gray-600 text-sm leading-relaxed pt-3">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function StudentSupport() {
  const { t } = useSiteLanguage();

  const SUPPORT_RESOURCES = [
    { icon: HeadphonesIcon, titleKey: 'support.tutor.title',       descKey: 'support.tutor.desc',       ctaKey: 'support.tutor.cta' },
    { icon: BookOpen,       titleKey: 'support.materials.title',   descKey: 'support.materials.desc',   ctaKey: 'support.materials.cta' },
    { icon: FileText,       titleKey: 'support.assignments.title', descKey: 'support.assignments.desc', ctaKey: 'support.assignments.cta' },
    { icon: MessageCircle,  titleKey: 'support.adviser.title',     descKey: 'support.adviser.desc',     ctaKey: 'support.adviser.cta' },
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title="Student Support"
        description="Dedicated tutor support, 24/7 portal access, and enrolment advisers — see how Atlas Academy supports you throughout your online course."
        path="/student-support"
      />
      {/* Hero */}
      <div className="bg-[#0f2b4a] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-blue-300 text-sm font-medium mb-2">{t('support.hero.eyebrow')}</p>
          <h1 className="text-3xl lg:text-5xl font-extrabold mb-4">{t('support.hero.title')}</h1>
          <p className="text-blue-100/70 max-w-xl text-lg leading-relaxed">
            {t('support.hero.desc')}
          </p>
        </div>
      </div>

      {/* Key support stats */}
      <div className="bg-blue-700 py-8">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-3 gap-4 text-center">
          {[
            { value: '24/7', labelKey: 'support.stats.portal' },
            { value: '< 48hrs', labelKey: 'support.stats.response' },
            { value: '1:1', labelKey: 'support.stats.tutor' },
          ].map((s, i) => (
            <div key={i}>
              <p className="text-2xl lg:text-3xl font-extrabold text-white">{s.value}</p>
              <p className="text-blue-200 text-xs mt-0.5">{t(s.labelKey)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Support resources */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">{t('support.resources.eyebrow')}</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('support.resources.title')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SUPPORT_RESOURCES.map(({ icon: Icon, titleKey, descKey, ctaKey }) => (
              <div key={titleKey} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow flex flex-col">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Icon size={22} className="text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{t(titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed flex-1">{t(descKey)}</p>
                <Link to="/contact" className="mt-4 text-blue-600 text-sm font-medium hover:underline flex items-center gap-1">
                  {t(ctaKey)} <ArrowRight size={13} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wellbeing section */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">{t('support.wellbeing.eyebrow')}</p>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('support.wellbeing.title')}</h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {t('support.wellbeing.desc')}
              </p>
              <ul className="space-y-2">
                {[
                  t('support.wellbeing.point1'),
                  t('support.wellbeing.point2'),
                  t('support.wellbeing.point3'),
                  t('support.wellbeing.point4'),
                ].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle size={15} className="text-blue-600 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl overflow-hidden h-64">
              <img
                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=500&fit=crop&q=80"
                alt="Student support"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Portal preview */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-[#0f2b4a] rounded-2xl p-8 lg:p-12 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-blue-300 text-sm font-medium mb-2">{t('support.portal.eyebrow')}</p>
                <h2 className="text-2xl font-bold mb-4">{t('support.portal.title')}</h2>
                <p className="text-blue-100/70 text-sm leading-relaxed mb-5">
                  {t('support.portal.desc')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: BookOpen,      labelKey: 'support.portal.feature1' },
                    { icon: Users,         labelKey: 'support.portal.feature2' },
                    { icon: Clock,         labelKey: 'support.portal.feature3' },
                    { icon: CheckCircle,   labelKey: 'support.portal.feature4' },
                  ].map(({ icon: Icon, labelKey }) => (
                    <div key={labelKey} className="flex items-center gap-2 text-blue-200 text-sm">
                      <Icon size={15} className="text-blue-400" /> {t(labelKey)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                <p className="text-white font-semibold mb-3 text-sm">{t('support.portal.enrolled')}</p>
                <p className="text-blue-200/70 text-xs mb-4">{t('support.portal.enrolledDesc')}</p>
                <button className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-lg text-sm font-semibold transition-colors">
                  {t('support.portal.accessBtn')}
                </button>
                <p className="text-center text-xs text-blue-300/60 mt-2">{t('support.portal.notEnrolled')} <Link to="/courses" className="text-blue-300 hover:underline">{t('support.portal.browse')}</Link></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">{t('support.faqs.eyebrow')}</p>
            <h2 className="text-2xl font-bold text-gray-900">{t('support.faqs.title')}</h2>
          </div>
          <div className="space-y-3">
            {FAQS.slice(3, 9).map(faq => (
              <FAQItem
                key={faq.id}
                question={t(`faq.${faq.id}.q`, faq.question)}
                answer={t(`faq.${faq.id}.a`, faq.answer)}
              />
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/how-it-works" className="text-blue-600 hover:underline text-sm font-medium inline-flex items-center gap-1">
              {t('support.faqs.viewAll')} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f2b4a] py-14">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">{t('support.cta.title')}</h2>
          <p className="text-blue-200/70 mb-7 max-w-md mx-auto">{t('support.cta.desc')}</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors">
            {t('support.cta.btn')} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
